/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import AccountService from 'cio/services/account';
import { GRAY_300, PURPLE_200 } from 'cio/utils/colors';
import { DataFlow } from 'cio/utils/integrations/static-integrations';

interface IntegrationsOctopusArgs {
  container: HTMLDivElement;
}

const SVG_LOGO_SIZE = 80;
const PATH_SAFETY_MARGIN = 10;

// Generates a "d" string for a <path> element, by:
// 1. Moving to the start coordinates
// 2. Drawing a Bezier curve for the first half of the path
// 3. Drawing the second half of the path as a "smooth curve", which simply extends the previous Bezier curve
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#cubic_b%C3%A9zier_curve
function generatePathString(startCoordinates = { x: 0, y: 0 }, endCoordinates = { x: 0, y: 0 }): string {
  let startX = startCoordinates.x;
  let startY = startCoordinates.y;

  let endX = endCoordinates.x;
  let endY = endCoordinates.y;

  // We anchor control points at thirds of the way along width of the available
  // space
  let horizontalBezierControlOffset = (endX - startX) / 3;

  let moveCommand = `M${startX},${startY}`;

  let bezierControlPoints = {
    x1: Math.round(startX + horizontalBezierControlOffset),
    y1: Math.round(startY),
    x2: Math.round(startX + (endX - startX) / 2 - 20), // 20 is an extra offset which was found experimentally, for visual balance
    y2: Math.round(startY),
    x: Math.round(startX + (endX - startX) / 2),
    y: Math.round(startY + (endY - startY) / 2),
  };
  let bezierCommand = `C${bezierControlPoints.x1},${bezierControlPoints.y1} ${bezierControlPoints.x2},${bezierControlPoints.y2} ${bezierControlPoints.x},${bezierControlPoints.y}`;

  let smoothCurveControlPoints = {
    x2: Math.round(startX + horizontalBezierControlOffset * 2),
    y2: Math.round(endY),
    x: Math.round(endX),
    y: Math.round(endY),
  };
  let smoothCurveCommand = `S${smoothCurveControlPoints.x2},${smoothCurveControlPoints.y2} ${smoothCurveControlPoints.x},${smoothCurveControlPoints.y}`;

  return `${moveCommand} ${bezierCommand} ${smoothCurveCommand}`;
}

const STROKE_SETTINGS = {
  enabled: {
    color: PURPLE_200,
    dasharray: '0 0',
    width: '3',
  },
  disabled: {
    color: GRAY_300,
    dasharray: '4 4',
    width: '1',
  },
};

function generateStrokeSettings(isEnabled: boolean): StrokeSettings {
  return STROKE_SETTINGS[isEnabled ? 'enabled' : 'disabled'];
}

function generatePathEdgeCoordinates({
  direction,
  svgContainerRect,
  containerRect,
  elementRect,
}: {
  direction: DataFlow;
  svgContainerRect: DOMRect;
  containerRect: DOMRect;
  elementRect: DOMRect;
}) {
  let centerCoordinates = {
    x: svgContainerRect.width / 2,
    y: svgContainerRect.height / 2,
  };

  let elementCenterTopOffset = elementRect.top - containerRect.top + elementRect.height / 2;

  let startCoordinates = {
    x: 0,
    y: 0,
  };

  let endCoordinates = {
    x: 0,
    y: 0,
  };

  if (direction === DataFlow.in) {
    // Start the path at the vertical center of the integration item, and with a
    // safety margin so the animated dots (if any) don't appear out of nowhere
    startCoordinates = {
      x: -PATH_SAFETY_MARGIN,
      y: elementCenterTopOffset,
    };
    // End the path at the left edge of the CIO Logo in the center, with a small
    // margin
    endCoordinates = {
      x: centerCoordinates.x - SVG_LOGO_SIZE / 2 + PATH_SAFETY_MARGIN,
      y: centerCoordinates.y,
    };
  } else if (direction === DataFlow.out) {
    // Start the path at the right edge of the CIO Logo, offset by a margin
    startCoordinates = {
      x: centerCoordinates.x + SVG_LOGO_SIZE / 2 - PATH_SAFETY_MARGIN,
      y: centerCoordinates.y,
    };
    // End the path at the vertical center of the integration item, plus the
    // margin
    endCoordinates = {
      x: svgContainerRect.width + PATH_SAFETY_MARGIN,
      y: elementCenterTopOffset,
    };
  }

  startCoordinates = {
    x: Math.round(startCoordinates.x),
    y: Math.round(startCoordinates.y),
  };
  endCoordinates = {
    x: Math.round(endCoordinates.x),
    y: Math.round(endCoordinates.y),
  };

  return {
    start: startCoordinates,
    end: endCoordinates,
  };
}

function generateDotSettings({
  id,
  pathCoordinates,
  direction,
}: {
  id: string;
  pathCoordinates: {
    start: {
      x: number;
      y: number;
    };
    end: {
      x: number;
      y: number;
    };
  };
  direction: DataFlow;
}): PathDotSettings {
  return {
    id: `${id}_dot`,
    direction,
    x: pathCoordinates.start.x,
    y: pathCoordinates.start.y,
    pathId: '',
  };
}

export type StrokeSettings = {
  color: string;
  dasharray: string;
  width: string;
};

export type PathSettings = {
  id: string;
  d: string;
  stroke: StrokeSettings;
  direction: DataFlow;
};

export type PathDotSettings = {
  id: string;
  direction: DataFlow;
  x: number;
  y: number;
  pathId: string;
};

export default class IntegrationsOctopus extends Component<IntegrationsOctopusArgs> {
  @service account!: AccountService;

  @tracked containerWidth: number | null = null;

  LOGO_SIZE = SVG_LOGO_SIZE;

  svgContainerElement: HTMLDivElement | null = null;
  resizeObserver: ResizeObserver | null = null;

  get showPremiumLabel() {
    return this.account.planFeatures.warehouseSync.showPremiumLabel;
  }

  @action
  didInsertSVGContainer(svgContainerElement: HTMLDivElement) {
    this.svgContainerElement = svgContainerElement;

    this.setSVGSize();
    this.attachResizeObserver();
  }

  willDestroy() {
    super.willDestroy();

    this.resizeObserver?.disconnect();
  }

  @tracked svgSize = { width: 0, height: 0 };

  get svgLogoPosition() {
    let { width, height } = this.svgSize;

    let x = width / 2 - SVG_LOGO_SIZE / 2;
    let y = height / 2 - SVG_LOGO_SIZE / 2;

    return {
      x,
      y,
    };
  }

  @action
  setSVGSize() {
    if (this.svgContainerElement == null) {
      return;
    }

    let { width, height } = this.svgContainerElement.getBoundingClientRect();
    this.svgSize = { width, height };
  }

  @action
  attachResizeObserver() {
    if (this.svgContainerElement == null) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.onResize));
    this.resizeObserver.observe(this.svgContainerElement);
  }

  @action
  onResize() {
    this.setSVGSize();
    this.generatePathsAndDots();
  }

  @tracked paths: PathSettings[] = [];
  @tracked pathDots: PathDotSettings[] = [];

  generatePathsAndDots() {
    if (this.svgContainerElement == null) {
      return;
    }

    let { container } = this.args;
    let containerRect = container.getBoundingClientRect();
    let svgContainerRect = this.svgContainerElement.getBoundingClientRect();

    let paths: PathSettings[] = [];
    let pathDots: PathDotSettings[] = [];

    let integrationElements = Array.from(container.querySelectorAll('.js-integration-item'));
    integrationElements.forEach((element, index) => {
      let idBase = `integration_${index}`;

      let isEnabled = element.hasAttribute('data-integration-enabled');
      let stroke = generateStrokeSettings(isEnabled);
      let direction = element.getAttribute('data-integration-direction') as DataFlow;
      let elementRect = element.getBoundingClientRect();

      let coordinates = generatePathEdgeCoordinates({ direction, svgContainerRect, containerRect, elementRect });

      let isPremium = element.hasAttribute('data-integration-is-premium');

      let path = {
        id: `${idBase}_path`,
        d: generatePathString(coordinates.start, coordinates.end),
        isPremium,
        stroke,
        direction,
      };
      paths.push(path);

      if (isEnabled) {
        let dotSettings = generateDotSettings({
          id: idBase,
          pathCoordinates: coordinates,
          direction,
        });

        let dot = { ...dotSettings, pathId: path.id };
        pathDots.push(dot);
      }
    });

    this.paths = paths;
    this.pathDots = pathDots;

    this.containerWidth = containerRect.width;
  }
}
